import { Metamodel } from 'app/model/base/model';
import { type DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { type DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { Owned } from 'app/model/base/named';
import { DataFlowDSLAdapterRegistry, Dataset, LinkedService } from 'shell-models-lib';
import {
    CosmosDBStartFrom,
    CosmosDBStoreType,
    CosmosSqlSettings
} from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { DataFlowExpressionUtils } from '../expression.util';
import { BooleanDataFlowType, IntegerDataFlowType, TimeStampDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { TabularTypeAdapter } from './tabular-type.adapter';
import { TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DatasetUtils, getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(CosmosSqlSettings)
})
export class CosmosSqlAdapter extends TabularTypeAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], _isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined): void {
        if (linkedService) {
            const dbName = DatasetUtils.getDatabaseFromLinkedService(linkedService);
            if (dbName) {
                props.push(`database: ${DataFlowAdapterUtils.formatExpressionValue(dbName)}`);
            }
        }

        if (isInline || !dataset) {
            return;
        }

        const container = getAttributeValueFromDataset(dataset, 'tableName');
        if (container) {
            props.push(`container: ${DataFlowAdapterUtils.formatExpressionValue(container)}`);
        }
    }

    public async toSourceDSL(setting: CosmosSqlSettings): Promise<string[]> {
        const props = [];
        if (__IS_FABRIC_MDF__ && !stringIsNullOrEmpty(setting.databaseName?.expression)) {
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.databaseName)}`);
        }
        if (setting.tableName.expression) {
            props.push(`container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        }
        if (setting.storeType) {
            props.push(`storeType: '${setting.storeType}'`);
        }
        if (setting.useQuery) {
            props.push(`format: 'documentQuery'`);
            props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(setting.query)}`);
        } else {
            props.push(`format: 'document'`);
        }
        if (setting.storeType === CosmosDBStoreType.Analytical) {
            props.push('enableChangeFeed: true');
            switch (setting.startFrom) {
                case CosmosDBStartFrom.Beginning:
                    props.push('changeFeedStartFromTheBeginning: true');
                    break;
                case CosmosDBStartFrom.Now:
                    props.push('changeFeedStartFromTheBeginning: false');
                    break;
                case CosmosDBStartFrom.Timestamp:
                    props.push('changeFeedStartFromTheBeginning: false');
                    props.push(
                        `changeFeedStartTimestamp: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.changeFeedStartTimestamp,
                            TimeStampDataFlowType.TYPE
                        )}`
                    );
                    break;
                default:
            }
            if (isTruthy(setting.systemColumns)) {
                props.push(
                    `systemColumns: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        setting.systemColumns,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
            if (setting.readSchema) {
                props.push('readSchema: true');
            }
            props.push(
                `captureIntermediateUpdate: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.captureIntermediateUpdate,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `captureUserDeletes: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.captureUserDeletes,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `captureTxnTTLDeletes: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.captureTxnTTLDeletes,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(this._additionalConfigsToDSL(setting));
        } else {
            if (!setting.useQuery) {
                if (isTruthy(setting.enableChangeFeed)) {
                    props.push(
                        `enableChangeFeed: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.enableChangeFeed,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                    props.push(
                        `changeFeedStartFromTheBeginning: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.changeFeedStartFromBeginning,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }
                if (isTruthy(setting.systemColumns)) {
                    props.push(
                        `systemColumns: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.systemColumns,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }
            }
        }

        if (!stringIsNullOrEmpty(setting.batchSize.expression)) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (!stringIsNullOrEmpty(setting.throughput.expression) && setting.storeType !== CosmosDBStoreType.Analytical) {
            props.push(
                `throughput: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.throughput,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (setting.preferredRegions != null && setting.preferredRegions.length > 0) {
            props.push(`preferredRegions: [${setting.preferredRegions.map((v) => `'${v}'`).join(',')}]`);
        }
        props.push(`store: 'cosmosDB'`);
        return props;
    }

    public async toSinkDSL(setting: CosmosSqlSettings): Promise<string[]> {
        const props = [];
        props.push(`format: 'document'`);
        if (__IS_FABRIC_MDF__ && !stringIsNullOrEmpty(setting.databaseName?.expression)) {
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.databaseName)}`);
        }
        if (setting.tableName.expression) {
            props.push(`container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        }
        const sink = setting.owner as DataFlowSink;
        if (sink.alterRowEnabled) {
            props.push(
                `deletable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowDelete,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `insertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowInsert,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `updateable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowUpdate,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `upsertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowUpsert,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        if (sink.requiresKeyColumns) {
            const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(setting);
            if (keyColumns) {
                props.push(keyColumns);
            }
        }

        if (setting.tableActionOption === TableActionOption.Recreate) {
            props.push('recreate: true');
        }
        if (setting.tableActionOption === TableActionOption.Truncate) {
            props.push(`truncate: true`);
        }

        if (setting.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (setting.partitionKey.expression) {
            props.push(`partitionKey: [${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.partitionKey)}]`);
        }
        if (setting.throughput.expression) {
            props.push(
                `throughput: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.throughput,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (setting.writeThroughPutBudget.expression) {
            props.push(
                `writeThroughPutBudget: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.writeThroughPutBudget,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        props.push(`store: 'cosmosDB'`);
        return props;
    }

    public async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<CosmosSqlSettings> {
        const setting = (await super.toSourceModel(owner, configs, handledConfigs)) as CosmosSqlSettings;
        if (configs.has('format')) {
            setting.useQuery = configs.get('format') === `'documentQuery'`;
        }
        if (configs.has('container')) {
            setting.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('container'), owner);
            handledConfigs.push('container');
        }
        setting.storeType = configs.has('storeType')
            ? (configs.get('storeType').trim().slice(1, -1) as CosmosDBStoreType)
            : undefined;
        if (configs.has('enableChangeFeed')) {
            setting.enableChangeFeed = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('enableChangeFeed'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('enableChangeFeed');
        }
        if (configs.has('changeFeedStartFromTheBeginning')) {
            setting.changeFeedStartFromBeginning = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('changeFeedStartFromTheBeginning'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('changeFeedStartFromTheBeginning');
        }

        if (isTruthy(setting.changeFeedStartFromBeginning)) {
            setting.startFrom = CosmosDBStartFrom.Beginning;
        } else if (configs.has('changeFeedStartTimestamp')) {
            setting.changeFeedStartTimestamp = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('changeFeedStartTimestamp'),
                owner,
                TimeStampDataFlowType.TYPE
            );
            setting.startFrom = CosmosDBStartFrom.Timestamp;
        } else {
            setting.startFrom = CosmosDBStartFrom.Now;
        }
        if (configs.has('systemColumns')) {
            setting.systemColumns = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('systemColumns'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('systemColumns');
        }
        if (configs.has('batchSize')) {
            setting.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        if (configs.has('throughput') && setting.storeType !== CosmosDBStoreType.Analytical) {
            setting.throughput = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('throughput'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('throughput');
        }
        if (configs.has('preferredRegions')) {
            setting.preferredRegions = DataFlowExpressionUtils.parseStringArrayInDsl(configs.get('preferredRegions'));
            handledConfigs.push('preferredRegions');
        }
        if (configs.has('captureIntermediateUpdate')) {
            setting.captureIntermediateUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('captureIntermediateUpdate'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('captureIntermediateUpdate');
        }
        if (configs.has('captureUserDeletes')) {
            setting.captureUserDeletes = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('captureUserDeletes'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('captureUserDeletes');
        }
        if (configs.has('captureTxnTTLDeletes')) {
            setting.captureTxnTTLDeletes = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('captureTxnTTLDeletes'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('captureTxnTTLDeletes');
        }
        if (configs.has('additionalConfigs')) {
            setting.additionalConfigs = this._additionalConfigsToModel(configs.get('additionalConfigs'), owner);
        }
        return setting;
    }

    public async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<CosmosSqlSettings> {
        const setting = (await super.toSinkModel(owner, configs, handledConfigs)) as CosmosSqlSettings;
        if (configs.has('container')) {
            setting.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('container'), owner);
            handledConfigs.push('container');
        }
        if (configs.has('batchSize')) {
            setting.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        if (configs.has('partitionKey')) {
            setting.partitionKey = DataFlowExpressionUtils.parseExpressionArrayInDsl(
                configs.get('partitionKey'),
                owner
            )[0];
            handledConfigs.push('partitionKey');
        }
        if (configs.has('throughput')) {
            setting.throughput = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('throughput'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('throughput');
        }
        if (configs.has('writeThroughPutBudget')) {
            setting.writeThroughPutBudget = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('writeThroughPutBudget'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('writeThroughPutBudget');
        }

        return setting;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): CosmosSqlSettings {
        const result = Owned.create<CosmosSqlSettings>(CosmosSqlSettings.MODEL, owner);
        result.initialize(undefined);
        return result;
    }
}
